<!--
  SuperAdmin Phase 3: Risk Watch.

  Surfaces the queue of distributors flagged by the Risk Watch triggers
  (payment_failed, inactive_30d, volume_drop, cancellation_request). Rows
  group by severity so a SA can clear the high-severity backlog first.
  Each row links to the SA distributor edit page /admin/distributors/:id/edit.

  Cross-reference: gap-analysis plan 1 section 1.3 + ADR 0013 (4 triggers).

  B1: wired to GET /api/superadmin/risk-events (open events only). The
  MockDataBanner is gone -- this is live data.

  ASCII-only.
-->
<template>
    <div data-testid="sa-risk-watch">
        <h1 class="sa-page-title">Risk Watch</h1>

        <p
            v-if="loading"
            class="sa-risk-state"
            data-testid="sa-risk-loading"
        >
            Loading...
        </p>
        <p
            v-else-if="errorMsg"
            class="sa-risk-state sa-risk-state--error"
            data-testid="sa-risk-error"
        >
            {{ errorMsg }}
        </p>
        <p
            v-else-if="groups.length === 0"
            class="sa-risk-state"
            data-testid="sa-risk-empty"
        >
            No open risk events. All clear.
        </p>

        <section
            v-for="group in groups"
            :key="group.severity"
            :data-testid="`sa-risk-group-${group.severity}`"
            class="sa-risk-group"
        >
            <h2
                class="sa-risk-group__title"
                :class="`sa-risk-group__title--${group.severity}`"
            >
                {{ severityLabel(group.severity) }}
                <span class="sa-risk-group__count">({{ group.events.length }})</span>
            </h2>

            <table class="sa-table">
                <thead>
                    <tr>
                        <th>Distributor</th>
                        <th>Event</th>
                        <th>Occurred</th>
                        <th>Summary</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="e in group.events"
                        :key="e.id"
                        data-testid="sa-risk-event-row"
                        class="sa-table__row"
                    >
                        <td>{{ e.distributor_name }}</td>
                        <td>{{ formatEventType(e.event_type) }}</td>
                        <td>{{ formatDate(e.occurred_at) }}</td>
                        <td>{{ e.summary }}</td>
                        <td>
                            <router-link
                                data-testid="sa-risk-view-link"
                                :to="`/admin/distributors/${e.distributor_id}/edit`"
                                class="sa-link"
                                >View distributor</router-link
                            >
                        </td>
                    </tr>
                </tbody>
            </table>
        </section>
    </div>
</template>

<script>
import api from "@/services/api";

const SEVERITY_ORDER = ["high", "medium", "low"];

function formatDate(iso) {
    return iso ? iso.slice(0, 10) : "";
}

function formatEventType(type) {
    // payment_failed -> "Payment failed", etc. Replaces underscores then
    // upper-cases the first letter so the event type reads naturally
    // without a separate label dictionary.
    const t = type.replace(/_/g, " ");
    return t.charAt(0).toUpperCase() + t.slice(1);
}

function severityLabel(sev) {
    return sev.charAt(0).toUpperCase() + sev.slice(1) + " severity";
}

// Group open events by severity (high -> medium -> low), skipping empty
// severities and sorting each group most-recent-first.
function groupBySeverity(events) {
    const byseverity = {};
    for (const e of events) {
        if (!byseverity[e.severity]) byseverity[e.severity] = [];
        byseverity[e.severity].push(e);
    }
    return SEVERITY_ORDER.filter(
        (s) => byseverity[s] && byseverity[s].length > 0,
    ).map((s) => ({
        severity: s,
        events: byseverity[s]
            .slice()
            .sort((a, b) =>
                (b.occurred_at || "").localeCompare(a.occurred_at || ""),
            ),
    }));
}

export default {
    name: "SuperAdminRiskWatch",
    data() {
        return {
            loading: true,
            errorMsg: "",
            groups: [],
        };
    },
    async mounted() {
        await this.fetchEvents();
    },
    methods: {
        formatDate,
        formatEventType,
        severityLabel,
        async fetchEvents() {
            this.loading = true;
            this.errorMsg = "";
            try {
                const resp = await api.get("/superadmin/risk-events");
                const data = resp && resp.data ? resp.data : resp;
                const rows = (data && data.rows) || [];
                this.groups = groupBySeverity(rows);
            } catch (e) {
                this.errorMsg = "Could not load risk events. Please try again.";
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 16px;
    color: #1a202c;
}
.sa-risk-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-risk-state--error {
    color: #c53030;
}
.sa-risk-group {
    margin-bottom: 24px;
}
.sa-risk-group__title {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 8px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}
.sa-risk-group__title--high {
    color: #c53030;
}
.sa-risk-group__title--medium {
    color: #b7791f;
}
.sa-risk-group__title--low {
    color: #2c5282;
}
.sa-risk-group__count {
    color: var(--tf-fg-4);
    font-weight: 400;
    margin-left: 6px;
}
.sa-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
}
.sa-table th {
    text-align: left;
    background: #f7fafc;
    color: #2d3748;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 8px 12px;
    border-bottom: 1px solid #e2e8f0;
}
.sa-table__row td {
    padding: 10px 12px;
    border-bottom: 1px solid #edf2f7;
    font-size: 14px;
    color: #1a202c;
    vertical-align: top;
}
.sa-table__row:last-child td {
    border-bottom: none;
}
.sa-link {
    color: #2b6cb0;
    text-decoration: none;
    font-size: 13px;
}
.sa-link:hover {
    text-decoration: underline;
}
</style>
